// @ts-nocheck
/* ===================================================================
   MediCooler Keuzehulp — decision-tree engine (vanilla JS)
   -------------------------------------------------------------------
   - Pure client-side, no backend.
   - Routing (which question/result follows which answer) lives here
     and is version controlled. All display text — questions, option
     labels/descriptions, result copy, UI chrome — is locale-dependent
     and is injected per section from sections/mc-keuzehulp.liquid via
     a <script type="application/json"> island, sourced from the
     shop's locale files so the quiz runs in any shop language.
   - Each section instance gets its own isolated state, so the widget
     can appear more than once on a page and survives theme-editor
     re-rendering (shopify:section:load).
   =================================================================== */
(function () {
  'use strict';

  /* ───────────────────────────────────────────────────────────────────
     DECISION TREE — each node lists the node/result ids its options
     lead to, in display order. Display text for a node and its
     options comes from the injected config (cfg.questions[nodeId]),
     matched to this array by index.
     ─────────────────────────────────────────────────────────────────── */
  const NODES = {
    start:              { next: ['fridge_dest', 'easybag_qty'] },
    easybag_qty:        { next: ['recommendation_easybag_single', 'recommendation_easybag_classic'] },
    fridge_dest:        { next: ['multi_stop', 'transport_mode'] },
    multi_stop:         { next: ['trip_duration', 'freezer_check'] },
    freezer_check:      { next: ['trip_duration', 'transport_mode'] },
    trip_duration:      { next: ['short_qty', 'mid_qty', 'long_24_qty', 'long_36_qty'] },
    short_qty:          { next: ['recommendation_weekender', 'recommendation_vacpack', 'recommendation_medicube_compact', 'recommendation_medicube_ultra'] },
    mid_qty:            { next: ['recommendation_vacpack', 'recommendation_medicube_compact', 'recommendation_medicube_ultra'] },
    long_24_qty:        { next: ['recommendation_prestige', 'recommendation_medicube_compact', 'recommendation_medicube_ultra'] },
    long_36_qty:        { next: ['recommendation_medicube_compact', 'recommendation_medicube_ultra'] },
    transport_mode:     { next: ['fly_train_duration', 'recommendation_auto'] },
    fly_train_duration: { next: ['recommendation_combo_prestige', 'recommendation_combo_compact'] }
  };

  /* ───────────────────────────────────────────────────────────────────
     OUTCOMES — urlKey references a key in the injected products map
     (cfg.products), which carries the per-product data pulled from
     Shopify at render time: title, url, and the custom.tagline /
     custom.product_intro / custom.inhoud / custom.temperatuurbereik /
     custom.koeltijd metafields. Combo "why" narratives come from
     cfg.results[id].why; the "during trip" / "on location" combo
     labels are fixed UI copy (cfg.ui.duringTrip / cfg.ui.onLocation),
     applied positionally to each combo's products array.
     ─────────────────────────────────────────────────────────────────── */
  const RESULTS = {
    recommendation_easybag_single: { type: 'single', urlKey: 'easybag_single' },
    recommendation_easybag_classic: { type: 'single', urlKey: 'easybag_classic' },
    recommendation_weekender: { type: 'single', urlKey: 'lifeinabag12' },
    recommendation_vacpack: { type: 'single', urlKey: 'vacpack' },
    recommendation_prestige: { type: 'single', urlKey: 'prestige' },
    recommendation_medicube_compact: { type: 'single', urlKey: 'medicube_compact' },
    recommendation_medicube_ultra: { type: 'single', urlKey: 'medicube_ultra' },
    recommendation_auto: {
      type: 'auto',
      urlKey: 'lifeinabox',
      alt: { urlKey: 'dometic_cff12' }
    },
    recommendation_combo_prestige: {
      type: 'combo',
      products: [{ urlKey: 'prestige' }, { urlKey: 'lifeinabox' }]
    },
    recommendation_combo_compact: {
      type: 'combo',
      products: [{ urlKey: 'medicube_compact' }, { urlKey: 'lifeinabox' }]
    }
  };

  /* ───────────────────────────────────────────────────────────────────
     Adaptive progress — branches in the tree lead to results at
     different depths, so "total steps" isn't a fixed number. For any
     question node it's 1 (this question) + the longest remaining chain
     of questions across its options. Memoized once at module scope
     since NODES is static and shared by every quiz instance on the page.
     ─────────────────────────────────────────────────────────────────── */
  const depthCache = {};
  function depthToResult(nodeId) {
    const node = NODES[nodeId];
    if (!node) return 0;
    if (depthCache[nodeId] != null) return depthCache[nodeId];
    const depth = 1 + node.next.reduce(function (max, nextId) {
      return Math.max(max, depthToResult(nextId));
    }, 0);
    depthCache[nodeId] = depth;
    return depth;
  }

  /* esc — the only stateless helper with no content dependency, so it
     stays at module scope. Everything else that builds markup depends
     on the injected per-instance config and lives inside initQuiz. */
  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /* decodeEntities/deepDecode — Liquid's json filter HTML-entity-encodes
     characters like ' and & in string values (e.g. "isn't" becomes
     "isn&#39;t"). Because that JSON sits inside a
     <script type="application/json"> island, the browser never HTML-decodes
     it — script content is raw text — so JSON.parse hands back the literal
     entity text. Decoding once here keeps cfg holding plain text, which esc()
     then re-encodes exactly once when building markup. */
  function decodeEntities(str) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = str;
    return textarea.value;
  }

  function deepDecode(value) {
    if (typeof value === 'string') return decodeEntities(value);
    if (Array.isArray(value)) return value.map(deepDecode);
    if (value && typeof value === 'object') {
      const out = {};
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) out[key] = deepDecode(value[key]);
      }
      return out;
    }
    return value;
  }

  /* ───────────────────────────────────────────────────────────────────
     Per-instance initialisation
     ─────────────────────────────────────────────────────────────────── */
  function initQuiz(root) {
    if (!root || root.dataset.mcInitialized === 'true') return;
    root.dataset.mcInitialized = 'true';

    const appEl      = root.querySelector('[data-mc-app]');
    const progressEl = root.querySelector('[data-mc-progress]');
    if (!appEl || !progressEl) return;

    let cfg = { products: {}, questions: {}, results: {}, ui: {}, specLabels: [] };
    const cfgEl = root.querySelector('[data-mc-config]');
    if (cfgEl) {
      try { cfg = Object.assign(cfg, deepDecode(JSON.parse(cfgEl.textContent))); }
      catch (e) { /* fall back to defaults */ }
    }
    const PRODUCTS = cfg.products || {};
    const QUESTIONS = cfg.questions || {};
    const RESULT_COPY = cfg.results || {};
    const UI = cfg.ui || {};
    const SPEC_LABELS = cfg.specLabels || [];
    const SPECS_COLOR_SCHEME = cfg.specsColorScheme || 'scheme-3';

    function url(key) { return (PRODUCTS[key] && PRODUCTS[key].url) || '#'; }

    function productData(key) {
      const p = PRODUCTS[key] || {};
      return {
        name: p.name || '',
        tagline: p.tagline || '',
        why: p.why || '',
        image: p.image || '',
        specs: (p.specs || []).filter(function (s) { return !!s; })
      };
    }

    function badge(text) {
      return '<div class="mc-badge">' + esc(text) + '</div>';
    }

    /* opts: { small, name } — name (product title) is appended as a visually
       hidden suffix so repeated CTA links stay distinguishable for
       screen-reader users navigating by link list. */
    function ctaLink(href, opts) {
      opts = opts || {};
      const cls = 'button mc-keuzehulp__cta' + (opts.small ? ' mc-keuzehulp__cta--small' : '');
      const hiddenName = opts.name ? '<span class="visually-hidden"> — ' + esc(opts.name) + '</span>' : '';
      return '<a class="' + cls + '" href="' + esc(href) + '">' + esc(UI.cta) + hiddenName + '</a>';
    }

    /* Thumbnail + heading (+ optional tagline), linked to the product's PDP.
       opts: { headingClass, href, showTagline } */
    function resultHead(p, opts) {
      opts = opts || {};
      const text =
        '<h2 class="' + opts.headingClass + '">' + esc(p.name) + '</h2>' +
        (opts.showTagline && p.tagline ? '<div class="mc-keuzehulp__result-tagline">' + esc(p.tagline) + '</div>' : '');
      return (
        '<a class="mc-keuzehulp__result-head" href="' + esc(opts.href) + '">' +
          (p.image ? '<img class="mc-keuzehulp__thumb" src="' + esc(p.image) + '" alt="" width="100" height="100" loading="lazy">' : '') +
          '<div class="mc-keuzehulp__result-head-text">' + text + '</div>' +
        '</a>'
      );
    }

    function specColumns(specs, colorScheme) {
      const items = (specs || []).map(function (s, i) {
        return (
          '<div class="mc-columns__item color-' + esc(colorScheme) + '">' +
            '<h3 class="mc-columns__heading">' + esc(SPEC_LABELS[i] || '') + '</h3>' +
            '<div class="mc-columns__value">' + esc(s) + '</div>' +
          '</div>'
        );
      }).join('');
      return '<div class="mc-columns">' + items + '</div>';
    }

    /* Shared recommendation body used by both the single-product and the
       auto (car) result types: badge, linked header, why-copy, specs, CTA. */
    function recommendationBlock(p, href, specsColorScheme) {
      return (
        badge(UI.recommendationBadge) +
        resultHead(p, { headingClass: 'mc-keuzehulp__result-name', showTagline: true, href: href }) +
        '<div class="mc-keuzehulp__result-why">' + esc(p.why) + '</div>' +
        specColumns(p.specs, specsColorScheme) +
        '<div class="mc-keuzehulp__result-actions">' + ctaLink(href, { name: p.name }) + '</div>'
      );
    }

    function resetBtn() {
      return (
        '<button type="button" class="mc-keuzehulp__reset-btn" data-mc-action="restart" aria-label="' + esc(UI.restart) + '">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<path d="M3 12a9 9 0 0 1 15.3-6.4L21 8"></path>' +
            '<path d="M21 3v5h-5"></path>' +
            '<path d="M21 12a9 9 0 0 1-15.3 6.4L3 16"></path>' +
            '<path d="M3 21v-5h5"></path>' +
          '</svg>' +
        '</button>'
      );
    }

    const shopifySection = root.closest('[id^="shopify-section-"]');
    const STATE_KEY = 'mc-keuzehulp-state' + (shopifySection ? ':' + shopifySection.id : '');

    function saveState() {
      try {
        sessionStorage.setItem(STATE_KEY, JSON.stringify({ current: current, step: step, history: history }));
      } catch (e) {}
    }

    function clearState() {
      try { sessionStorage.removeItem(STATE_KEY); } catch (e) {}
    }

    function loadState() {
      try {
        const raw = sessionStorage.getItem(STATE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) { return null; }
    }

    let history = [];
    let current = 'start';
    let step = 0;

    const saved = loadState();
    if (saved && (NODES[saved.current] || RESULTS[saved.current])) {
      current = saved.current;
      step = typeof saved.step === 'number' ? saved.step : 0;
      history = Array.isArray(saved.history) ? saved.history : [];
    }

    function backBtn() {
      return history.length > 0
        ? '<div class="mc-keuzehulp__nav"><button type="button" class="mc-keuzehulp__back-btn" data-mc-action="back">&#8592; ' + esc(UI.back) + '</button></div>'
        : '';
    }

    function renderProgress() {
      const isQuestion = !!NODES[current];
      const total = step + (isQuestion ? depthToResult(current) : 0);
      const done = isQuestion ? step : total;
      const pct = total > 0 ? Math.round((done / total) * 100) : 100;
      const label = isQuestion
        ? String(UI.questionProgress || '').replace('__STEP__', String(step + 1)).replace('__TOTAL__', String(total))
        : String(UI.done || '');

      progressEl.innerHTML =
        '<span class="mc-keuzehulp__progress-label">' + esc(label) + '</span>' +
        '<div class="mc-keuzehulp__progress-track">' +
          '<div class="mc-keuzehulp__progress-fill" style="width: ' + pct + '%"></div>' +
        '</div>';

      progressEl.setAttribute('aria-valuemin', '0');
      progressEl.setAttribute('aria-valuemax', String(total));
      progressEl.setAttribute('aria-valuenow', String(done));
      progressEl.setAttribute('aria-valuetext', label);
    }

    function renderQuestion(nodeId, node) {
      const content = QUESTIONS[nodeId] || {};
      const opts = node.next.map(function (nextId, i) {
        const optContent = (content.opts && content.opts[i]) || {};
        return (
          '<button type="button" class="mc-keuzehulp__option" data-mc-action="choose" data-mc-next="' + esc(nextId) + '">' +
            '<div class="mc-keuzehulp__option-inner">' +
              '<span class="mc-keuzehulp__option-label">' + esc(optContent.label) + '</span>' +
              (optContent.desc ? '<span class="mc-keuzehulp__option-desc">' + esc(optContent.desc) + '</span>' : '') +
            '</div>' +
            '<div class="mc-keuzehulp__option-arrow" aria-hidden="true">&#8594;</div>' +
          '</button>'
        );
      }).join('');

      const stepLabel = String(UI.questionLabel || '').replace('__STEP__', String(step + 1));

      appEl.innerHTML =
        '<div class="mc-keuzehulp__card">' +
          resetBtn() +
          '<div class="mc-keuzehulp__step-label">' + esc(stepLabel) + '</div>' +
          '<h2>' + esc(content.q) + '</h2>' +
          (content.sub ? '<p class="mc-keuzehulp__subtitle">' + esc(content.sub) + '</p>' : '') +
          '<div class="mc-keuzehulp__options">' + opts + '</div>' +
          backBtn() +
        '</div>';
    }

    function renderSingle(r) {
      const p = productData(r.urlKey);
      appEl.innerHTML =
        '<div class="mc-keuzehulp__card">' +
          resetBtn() +
          recommendationBlock(p, url(r.urlKey), SPECS_COLOR_SCHEME) +
          backBtn() +
        '</div>';
    }

    function renderAuto(r) {
      const p = productData(r.urlKey);
      const altP = productData(r.alt.urlKey);
      const altHref = url(r.alt.urlKey);
      appEl.innerHTML =
        '<div class="mc-keuzehulp__card">' +
          resetBtn() +
          recommendationBlock(p, url(r.urlKey), SPECS_COLOR_SCHEME) +
          '<div class="mc-keuzehulp__alt-divider">' +
            '<div class="mc-keuzehulp__alt-line"></div>' +
            '<div class="mc-keuzehulp__alt-label">' + esc(UI.alsoAvailableForCar) + '</div>' +
            '<div class="mc-keuzehulp__alt-line"></div>' +
          '</div>' +
          '<div class="mc-keuzehulp__alt-block">' +
            resultHead(altP, { headingClass: 'mc-keuzehulp__alt-name', href: altHref }) +
            '<div class="mc-keuzehulp__alt-desc">' + esc(altP.why) + '</div>' +
            specColumns(altP.specs, SPECS_COLOR_SCHEME) +
            ctaLink(altHref, { small: true, name: altP.name }) +
          '</div>' +
          backBtn() +
        '</div>';
    }

    function renderCombo(r, resultId) {
      const comboLabels = [UI.duringTrip, UI.onLocation];
      const why = (RESULT_COPY[resultId] && RESULT_COPY[resultId].why) || '';
      const blocks = r.products.map(function (product, i) {
        const p = productData(product.urlKey);
        const href = url(product.urlKey);
        return (
          '<div class="mc-keuzehulp__product-block">' +
            badge(comboLabels[i]) +
            resultHead(p, { headingClass: 'mc-keuzehulp__product-name', href: href }) +
            specColumns(p.specs, SPECS_COLOR_SCHEME) +
            ctaLink(href, { small: true, name: p.name }) +
          '</div>'
        );
      }).join('');

      appEl.innerHTML =
        '<div class="mc-keuzehulp__card">' +
          resetBtn() +
          badge(UI.comboBadge) +
          '<h2 class="mc-keuzehulp__result-name">' + esc(UI.twoProducts) + '</h2>' +
          '<div class="mc-keuzehulp__result-why">' + esc(why) + '</div>' +
          blocks +
          backBtn() +
        '</div>';
    }

    function render() {
      renderProgress();
      if (NODES[current]) {
        renderQuestion(current, NODES[current]);
      } else if (RESULTS[current]) {
        const r = RESULTS[current];
        if (r.type === 'single') renderSingle(r);
        else if (r.type === 'auto') renderAuto(r);
        else if (r.type === 'combo') renderCombo(r, current);
      }
    }

    function choose(next) {
      if (!next) return;
      history.push({ node: current, step: step });
      current = next;
      step = step + 1;
      render();
      saveState();
    }

    function back() {
      if (!history.length) return;
      const prev = history.pop();
      current = prev.node;
      step = prev.step;
      render();
      saveState();
    }

    function restart() {
      history = [];
      current = 'start';
      step = 0;
      clearState();
      render();
    }

    root.addEventListener('click', function (e) {
      const el = e.target.closest('[data-mc-action]');
      if (!el || !root.contains(el)) return;
      const action = el.getAttribute('data-mc-action');
      if (action === 'choose') choose(el.getAttribute('data-mc-next'));
      else if (action === 'back') back();
      else if (action === 'restart') restart();
    });

    render();
  }

  function bootAll(scope) {
    const roots = (scope || document).querySelectorAll('[data-mc-root]');
    Array.prototype.forEach.call(roots, initQuiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootAll(); });
  } else {
    bootAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    bootAll(e.target);
  });
})();
